import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Row, Col, Card, Image } from "react-bootstrap";
import { IActionHandler, CreateUserData, MessageDisplayType, WebAuthnKeyRegister, UserIdentifier } from "@preveil-api";
import {
  QueryParamKeys, QueryParamValues, normalizeQueryUserId, useAppDispatch, useAppSelector, account_types, GlobalErrorMessages, MessageHandlerDisplayType, KeyStorageUser, CreateAccountSteps,
  AccountErrorMessages, Message, DefaultRoutes, MessageAnchors, StatusCode, AuthenticationTypes, collectionApi, Account, AppUserKey, usePostUsersVerifyMutation,
  useAuthContext
} from "src/common";
import { Loading, ProgressCircles } from "src/components";
import {
  EmailForm, VerificationForm, UserInformationForm, PasswordForm, PhoneForm, SMSCodeVerificationForm, Pick2FAForm, TOTPForm, ExpressUserAlert, ErrorPanel,
  WebAuthKeyAdded
} from ".";
import { uiActions, accountActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";
import { authenticator } from "otplib";
import apple from "src/assets/images/account/Apple.png";
import authy from "src/assets/images/account/Authy.png";
import duo from "src/assets/images/account/Duo.png";
import google from "src/assets/images/account/Google.png";
import microsoft from "src/assets/images/account/Microsoft.png";
import okta from "src/assets/images/account/Okta.svg";

const two_fa_methods = [
  { name: "Google", value: google },
  { name: "Apple", value: apple },
  { name: "Microsoft", value: microsoft },
  { name: "Authy", value: authy },
  { name: "Duo", value: duo },
  { name: "Okta", value: okta }
];


const step_display_numbers: { [key: number]: number } = {
  [CreateAccountSteps.SUBMIT_EMAIL]: 1,
  [CreateAccountSteps.SUBMIT_VERIFICATION_CODE]: 2,
  [CreateAccountSteps.SUBMIT_USER_INFORMATION]: 3,
  [CreateAccountSteps.SUBMIT_PASSWORD]: 4,
  [CreateAccountSteps.PICK_2FA_METHOD]: 5,
  [CreateAccountSteps.SUBMIT_PHONE]: 6,
  [CreateAccountSteps.SUBMIT_TOTP_CODE]: 6,
  [CreateAccountSteps.SUBMIT_SMS_CODE]: 7,
  [CreateAccountSteps.WEB_AUTHN_KEY_ADDED]: 6
};

function CreateExpressAccount() {
  const [search_params, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const current_account = useAppSelector((state: RootState) => state.account.current_account);
  const step = useAppSelector((state: RootState) => state.account.status);
  const [user_id, setUserId] = useState<string>();
  const [expires, setExpires] = useState<string>(new Date().toString());
  const [keystorage_user, setKeyStorageUser] = useState<KeyStorageUser>();
  const [create_user_data, setCreateUserData] = useState<CreateUserData>();
  const [user_identifier, setUserIdentifier] = useState<UserIdentifier>();
  const [totp_secret, setTOTPSecret] = useState<string>();
  const [verification_error_count, setVerificationErrorCount] = useState<number>(0);
  const [max_step_number, setMaxStepNumber] = useState<number>(6);
  // NOTE: Capture at mount time — after conversion completes, pending_account_type is cleared from the new account object,
  // so this must be stored in a ref before the useEffect([current_account]) fires with the updated account.
  const pending_convert = useRef(!!current_account && current_account.pending_account_type === account_types.express);
  const [verifyUser] = usePostUsersVerifyMutation();
  const [fetchKssAccountClaimReceipt] = collectionApi.endpoints.getKssAccountClaimReceipt.useLazyQuery();
  const { loginComplete } = useAuthContext();
  // Description: load CreateAccountEmailForm (First onload event)
  useEffect(() => {
    handleInitCreateExpressAccount();
    return () => { // On unmount destroy the KSS store info
      dispatch(uiActions.handleMessageDismiss());
      dispatch(accountActions.destroyAccountFormStates());
    };
  }, []);

  // Description: Update the CreateAccount status when current account is set in state
  useEffect(() => {
    (!!current_account) &&
    loginComplete(current_account, account_types.express, 
      `${DefaultRoutes.mail_default}?${QueryParamKeys.PAGE_ACTION_QUERY_KEY}=${pending_convert.current ? QueryParamValues.downgraded : QueryParamValues.new}`);
  }, [current_account]);


  // Description: Handle all children component actions and store it
  const CreateAccountRequests = {
    // Description: Already validated email 
    handleSubmitEmail: (params: { user_id: string; expires: string; status: string; }) => {
      setUserId(params.user_id);
      setExpires(params.expires);
      dispatch(accountActions.setComponentStatus(CreateAccountSteps.SUBMIT_VERIFICATION_CODE));
    },
    handleSubmitVerificationCode: (_create_user_data: CreateUserData) => {
      if (!!_create_user_data) {
        setCreateUserData(_create_user_data);
        // NOTE: reset message in case there are errors without watching ui.state.errors as it triggers a re-renders for the component
        dispatch(uiActions.handleMessageDismiss());
        dispatch(accountActions.setComponentStatus(CreateAccountSteps.SUBMIT_USER_INFORMATION));
      } else {
        CreateAccountRequests.handlePageError({ message: AccountErrorMessages.create_submit_verification_code });
      }
    },
    handleSubmitVerificationCodeError: (status: number) => {
      if (status === StatusCode.FORBIDDEN_DOMAIN && verification_error_count === 4) {
        dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.bad_code_reset)));
        setExpires(new Date().toString());
        setVerificationErrorCount(0);
        dispatch(accountActions.setComponentStatus(CreateAccountSteps.SUBMIT_EMAIL));
      } else {
        setVerificationErrorCount(verification_error_count + 1);
        dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.bad_verification_code)));
      }
    },
    handleSubmitUserInformation: (params: { first_name: string, last_name: string, display_name: string }) => {
      setUserIdentifier({
        user_id: user_id || "",
        first_name: params.first_name,
        last_name: params.last_name
      });
      setCreateUserData(Object.assign({}, create_user_data, { display_name: params.display_name }));
      dispatch(accountActions.setComponentStatus(CreateAccountSteps.SUBMIT_PASSWORD));
    },
    // Description: Create a new KSS User to pass to phone component then on submit send to KSS 
    handleSubmitPassword: (password: string) => {
      if (pending_convert.current) {
        if (!!current_account) {
          fetchKssAccountClaimReceipt({ account_ids: Account.getAccountIdentifiers(current_account) }).then((response) => {
            const account_claim_receipt = response.data?.account_claim_receipt;
            dispatch(accountActions.setComponentStatus(CreateAccountSteps.LOADING));
            return KeyStorageUser.initKeyStorageUser({
              user_id: current_account.user_id,
              account_claim_receipt: account_claim_receipt || "",
              display_name: current_account.display_name || "",
              password
            }, "", 0, current_account.user_key as AppUserKey)
              .then((keystorage_user: KeyStorageUser) => {
                setKeyStorageUser(keystorage_user);
                setTOTPSecret(authenticator.generateSecret(20));
                dispatch(accountActions.setComponentStatus(CreateAccountSteps.PICK_2FA_METHOD));
              }).catch((stack: unknown) => {
                CreateAccountRequests.handlePageError({ message: AccountErrorMessages.create_submit_password, stack });
              });
          });
        } else {
          CreateAccountRequests.handlePageError({ message: AccountErrorMessages.create_submit_password });
        }
      } else {
        if (!!create_user_data && !!create_user_data.account_claim_receipt) {
          const secret = create_user_data.secret;
          const key_version = Number(create_user_data.key_version);
          dispatch(accountActions.setComponentStatus(CreateAccountSteps.LOADING));
          return KeyStorageUser.initKeyStorageUser({
            user_id: create_user_data.user_id,
            account_claim_receipt: create_user_data.account_claim_receipt,
            display_name: create_user_data.display_name || "",
            password
          }, secret, key_version)
            .then((keystorage_user: KeyStorageUser) => {
              setKeyStorageUser(keystorage_user);
              setTOTPSecret(authenticator.generateSecret(20));
              dispatch(accountActions.setComponentStatus(CreateAccountSteps.PICK_2FA_METHOD));
            }).catch((stack: unknown) => {
              CreateAccountRequests.handlePageError({ message: AccountErrorMessages.create_submit_password, stack });
            });
        } else {
          CreateAccountRequests.handlePageError({ message: AccountErrorMessages.create_submit_password });
        }
      }
    },
    // Description: handles submission of phone number and resend code actions
    // Note: for resend SMS verification click (phone_number = undefined)
    handleSubmitPhone: (params: { expires: string; phone_number?: string; }) => {
      const phone_number = !!params.phone_number ? params.phone_number : keystorage_user?.phone_number;
      // Note: Step 6 will be set on reducer side
      dispatch(accountActions.setComponentStatus(CreateAccountSteps.LOADING));
      if (!!phone_number && !!keystorage_user) {
        setExpires(params.expires);
        keystorage_user.setPhoneNumber(phone_number);
        keystorage_user.setSMSCode("");
        dispatch(uiActions.handleMessageDismiss());
        dispatch(accountActions.setNewKSSUser({ keystorage_user }));
      } else {
        CreateAccountRequests.handlePageError({ message: AccountErrorMessages.create_express_account_default });
      }
    },
    handleSubmitSMSVerificationCode: (sms_code: string) => {
      dispatch(accountActions.setComponentStatus(CreateAccountSteps.LOADING));
      if (!!keystorage_user && !!sms_code) {
        // NOTE: reset message in case there are errors without watching ui.state.errors as it triggers a re-renders for the component
        dispatch(uiActions.handleMessageDismiss());
        keystorage_user.setSMSCode(sms_code);
        keystorage_user.setTwoFactorType(AuthenticationTypes.SMS);
        if (pending_convert.current) {
          dispatch(accountActions.setNewKSSUser({ keystorage_user, current_account }));
        } else {
          dispatch(accountActions.setNewKSSUser({ keystorage_user }));
        }
      } else {
        CreateAccountRequests.handlePageError({ message: AccountErrorMessages.create_express_account_validation });
      }
    },

    handleSubmitTOTPVerificationCode: () => {
      if (!!keystorage_user && !!totp_secret) {
        keystorage_user.setTOTPSecret(totp_secret);
        keystorage_user.setTwoFactorType(AuthenticationTypes.TOTP);
        if (pending_convert.current) {
          dispatch(accountActions.setNewKSSUser({ keystorage_user, current_account }));
        } else {
          dispatch(accountActions.setNewKSSUser({ keystorage_user }));
        }
      } else {
        CreateAccountRequests.handlePageError({ message: AccountErrorMessages.create_express_account_validation });
      }
    },
    handleSubmitWebAuthnKey: (params: { web_authn_key: WebAuthnKeyRegister, type: AuthenticationTypes }) => {
      const { web_authn_key, type } = params;
      if (!!keystorage_user) {
        keystorage_user.setWebAuthnKey(web_authn_key);
        keystorage_user.setTwoFactorType(type);
        dispatch(accountActions.setNewKSSUser({ keystorage_user }));
      } else {
        CreateAccountRequests.handlePageError({ message: AccountErrorMessages.create_express_account_validation });
      }
    },
    // Description: Reset all form states and go back to first step
    handleResetForms: () => {
      setUserId(undefined);
      setKeyStorageUser(undefined);
      setCreateUserData(undefined);
      setUserIdentifier(undefined);
      dispatch(accountActions.revokeCurrentUser());
      dispatch(accountActions.setComponentStatus(CreateAccountSteps.SUBMIT_EMAIL));
    },
    handlePageError: (params: { message: string, stack?: any, displayType?: MessageDisplayType }) => {
      CreateAccountRequests.handleResetForms();
      const display_type = params.displayType || MessageHandlerDisplayType.toastr;
      dispatch(uiActions.handleRequestErrors(new Message(params.message, display_type), params.stack));
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
    },
    handleChangeStep: (step: number) => {
      dispatch(accountActions.setComponentStatus(step));
    }
  };

  //  Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    // Handle local calls:
    if ((CreateAccountRequests as any)[callback] instanceof Function) {
      (CreateAccountRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      CreateAccountRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }

  // --------------------------
  // General functions
  // --------------------------
  // Description: Initialize create express account component based on the query string information.
  function handleInitCreateExpressAccount() {
    const query = search_params.get(QueryParamKeys.USER_ID_QUERY_KEY);
    const query_user_id = normalizeQueryUserId(query);
    const secret = search_params.get(QueryParamKeys.SECRET_QUERY_KEY)?.trim();
    const key_version = search_params.get(QueryParamKeys.KEY_VERSION_KEY)?.trim();
    const account_type = search_params.get(QueryParamKeys.ACCOUNT_TYPE_KEY)?.trim();

    // NOTE: Handle express account claim flow
    if (!!query_user_id && !!secret && !!key_version && account_type === account_types.express) {
      handleClaimAdminAccount(query_user_id, secret);
      // NOTE: Remove all parameters
      setSearchParams({}, { replace: true });
      return;
    }

    // Description: Normal flow - start with email
    !!query_user_id && setUserId(query_user_id);
    if (!!query) {
      search_params.delete(QueryParamKeys.USER_ID_QUERY_KEY);
      setSearchParams(search_params);
    }
    if (pending_convert.current) {
      dispatch(accountActions.setComponentStatus(CreateAccountSteps.SUBMIT_PASSWORD));
    } else {
      dispatch(accountActions.setComponentStatus(CreateAccountSteps.SUBMIT_EMAIL));
    }
  }

  // Description: Handle express account claim flow
  async function handleClaimAdminAccount(query_user_id: string, secret: string) {
    try {
      // NOTE: Verify user and get claim data
      const verify_response = await verifyUser({ user_id: query_user_id, secret }).unwrap();
      const _create_user_data: CreateUserData = {
        user_id: query_user_id,
        secret,
        key_version: verify_response.key_version,
        display_name: verify_response.display_name,
        account_claim_receipt: verify_response.account_claim_receipt,
        org_name: verify_response.org_name,
      };
      // NOTE: Set state and skip to password step
      setUserId(query_user_id);
      setCreateUserData(_create_user_data);
      // Description: Parse display name for user identifier
      if (verify_response.display_name) {
        const name_parts = verify_response.display_name.split(" ");
        setUserIdentifier({
          user_id: query_user_id,
          first_name: name_parts[0] || "",
          last_name: name_parts.slice(1).join(" ") || ""
        });
      }
      dispatch(accountActions.setComponentStatus(CreateAccountSteps.SUBMIT_PASSWORD));
    } catch (error) {
      CreateAccountRequests.handlePageError({ message: AccountErrorMessages.claim_account_missing_params, stack: error });
    }
  }

  // Description: Render the appropriate panel corresponding to the step in store
  function CreateAccountForms() {
    // Update the max step number based on the user's choice
    useEffect(() => {
      if (step === CreateAccountSteps.PICK_2FA_METHOD) {
        setMaxStepNumber(6);
      } else if (step === CreateAccountSteps.SUBMIT_PHONE) {
        setMaxStepNumber(step + 1);
      }
    }, [step]);

    switch (step) {
      case CreateAccountSteps.SUBMIT_EMAIL:
      case CreateAccountSteps.SUBMIT_ERROR:
        return <EmailForm handleAction={handlePageActions} user_id={user_id}>
          <>
            <h3 className="border-bottom">Welcome to PreVeil Express!</h3>
            <p>Please enter your email address that will serve as your PreVeil <b>User ID.</b></p>
            <p>PreVeil will send an email to this address for you to create an account.</p>
          </>
        </EmailForm>;
      case CreateAccountSteps.SUBMIT_VERIFICATION_CODE:
        return !!user_id ? <VerificationForm handleAction={handlePageActions} user_id={user_id} expires={expires} /> :
          <ErrorPanel handleAction={handlePageActions} />;
      case CreateAccountSteps.SUBMIT_USER_INFORMATION:
        // NOTES:  if the user is joining an organization, give instructions to get a new invitation
        return (!!create_user_data && !!create_user_data.org_name) ?
          <ExpressUserAlert org_name={create_user_data.org_name} handleAction={handlePageActions} /> :
          <UserInformationForm handleAction={handlePageActions} />;
      case CreateAccountSteps.SUBMIT_PASSWORD:
        return <PasswordForm user_identifier={user_identifier} handleAction={handlePageActions} />;
      case CreateAccountSteps.PICK_2FA_METHOD:
        return (!!current_account && current_account instanceof Account) ?
          <Pick2FAForm handleAction={handlePageActions} user_id={current_account.user_id} display_name={current_account.display_name} /> :
          (!!user_id && !!create_user_data && !!create_user_data.display_name) ?
            <Pick2FAForm handleAction={handlePageActions} user_id={user_id} display_name={create_user_data.display_name} /> :
            <ErrorPanel handleAction={handlePageActions} />;
      case CreateAccountSteps.SUBMIT_TOTP_CODE:
        return (!!current_account && current_account instanceof Account && !!totp_secret) ?
          <TOTPForm handleAction={handlePageActions} user_id={current_account.user_id} totp_secret={totp_secret} /> :
          (!!user_id && !!totp_secret) ?
            <TOTPForm handleAction={handlePageActions} user_id={user_id} totp_secret={totp_secret} /> :
            <ErrorPanel handleAction={handlePageActions} />;
      case CreateAccountSteps.SUBMIT_PHONE:
        return <PhoneForm handleAction={handlePageActions} />;
      case CreateAccountSteps.SUBMIT_SMS_CODE:
        return <SMSCodeVerificationForm handleAction={handlePageActions} expires={expires} />;
      case CreateAccountSteps.WEB_AUTHN_KEY_ADDED:
        return !!keystorage_user ? 
          (!!current_account && current_account instanceof Account) ?
            <WebAuthKeyAdded kss_user={keystorage_user} current_account={current_account} /> :
            <WebAuthKeyAdded kss_user={keystorage_user} /> : 
          <ErrorPanel handleAction={handlePageActions} />;
      default:
        return <Loading className="in-place" />;
    }
  }

  // Description: Add section for adding 2FA 
  function twoFAInfo() {
    return <div className="two-factor-info ms-2">
      <p>PreVeil supports leading authenticator applications</p>
      {
        _.map(two_fa_methods, (method, i) => {
          return <Image data-tooltip-id="pv-tooltip"
            data-tooltip-place="bottom"
            data-tooltip-content={method.name} key={i} src={method.value} />;
        })
      }
    </div>;
  }

  // Description: Add side panel for express users claiming account
  function joinOrganization(org_name: string) {
    return <div className="join-organization-info ms-2">
      <h4>Account Setup</h4>
      <p>Your PreVeil Express user ID is <b>{user_id}</b>.</p>
      <p>You are now a member of the <b>{org_name}</b> organization.</p>
    </div>;
  }

  // Description: Add side panel for full users downgrading to express
  function downgrade() {
    return <div className="downgrade-info ms-2">
      <h4>Account Conversion</h4>
      <p>Your account is pending to be converted to an Express account.</p>
      <p>Remember to <b> save </b> your new recovery file when you log in.</p>
    </div>;
  }

  return step > 0 ?
    <Row className="create-account-card">
      <Col md={7}>
        <Card className="pv-card">
          <ProgressCircles total_steps={max_step_number + 1} current_step={step_display_numbers[step]} />
          <CreateAccountForms />
        </Card>
      </Col>
      <Col className="right-panel">
        {
          step === CreateAccountSteps.SUBMIT_TOTP_CODE && twoFAInfo()
        }
        {
          (step === CreateAccountSteps.SUBMIT_PASSWORD && !!create_user_data?.org_name) && joinOrganization(create_user_data.org_name)
        }
        {
          (step === CreateAccountSteps.SUBMIT_PASSWORD && pending_convert.current) && downgrade()
        }
      </Col>
    </Row> : <Loading />;
}

export default React.memo(CreateExpressAccount);
